import React, { FC, useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { EPopup } from '@src/types/common';
import { style, classes } from 'typestyle';
import { useMutationCreatorCountriesUpdate } from '@src/queries/media/useMutationCreatorCountriesUpdate';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import { popupsAtom, setCreatorCountriesAtom, setPopup } from '@src/state/popupAtoms';
import { PopupOverlay } from '@src/components/PopupOverlay';
import {
  buttonsClass,
  buttonBorderClass,
  buttonPrimaryClass,
} from '../CollectOverlay/components/sharedStyles';

const SetCreatorCountriesPopup: FC = () => {
  const show = useAtomValue(popupsAtom)[EPopup.setCreatorCountriesOverlay];
  const { mediaList } = useAtomValue(setCreatorCountriesAtom);
  const close = () => setPopup(EPopup.setCreatorCountriesOverlay, false);
  const { data: userConfig } = useQueryUserConfig();
  const catalogCountries = userConfig?.user?.catalogCountries;
  const [creatorCountries, setCreatorCountries] = useState<string[]>([]);
  const { mutate: mutateCreatorCountries, isPending: isSettingCreatorCountries } =
    useMutationCreatorCountriesUpdate();

  useEffect(() => {
    if (mediaList && mediaList.length > 1) {
      setCreatorCountries(catalogCountries ?? []);
    } else if (mediaList && mediaList.length === 1) {
      const allCreatorCountries = mediaList.flatMap(media => media.creatorCountries || []);
      const uniqueCreatorCountries = Array.from(new Set(allCreatorCountries));
      if (uniqueCreatorCountries.length === 0) {
        // If the media has no creator countries set, we want to check all countries by default
        setCreatorCountries(catalogCountries ?? []);
        return;
      }
      setCreatorCountries(uniqueCreatorCountries);
    } else {
      // By default, we want to check all countries when the media has no creator countries set
      setCreatorCountries(catalogCountries ?? []);
    }
  }, [mediaList]);

  const handleCountryToggle = (country: string) => {
    setCreatorCountries((prev) =>
      prev.includes(country) ? prev.filter((c) => c !== country) : [...prev, country]
    );
  };

  const handleSubmit = () => {
    const excludedCountries = catalogCountries?.filter((country) => !creatorCountries.includes(country));
    const mediaUsernames = Array.from(
      new Set(
        mediaList?.map(
          media => media.source.user_name
        ).filter(
          (username): username is string => username !== undefined && username !== ''
        ) ?? []
      )
    );

    mutateCreatorCountries(
      {
        updates: mediaUsernames.map(username => ({
          username,
          countries: creatorCountries,
          countriesToExclude: excludedCountries ?? [],
        })),
      },
      {
        onSuccess: () => {
          close();
        },
        onError: (error) => {
          console.error('Failed to update creator countries:', error);
        },
      }
    );
  };

  if (!mediaList || !catalogCountries) {
    return null;
  }

  return (
    <PopupOverlay
      show={!!show}
      onClose={close}
      title="Set Creator Countries"
      width="min(480px, 92vw)"
    >
      <div className={checkboxGroupClass}>
        {catalogCountries.map((country) => (
          <label key={country} className={checkboxLabelClass}>
            <input
              type="checkbox"
              value={country}
              checked={creatorCountries.includes(country)}
              onChange={() => handleCountryToggle(country)}
              disabled={isSettingCreatorCountries}
            />
            {country}
          </label>
        ))}
      </div>
      <div className={buttonsClass}>
        <button
          className={classes('button', buttonBorderClass)}
          onClick={() => close()}
          disabled={isSettingCreatorCountries}
        >
          Cancel
        </button>
        <button
          className={classes('button', buttonPrimaryClass)}
          onClick={handleSubmit}
          disabled={isSettingCreatorCountries}
        >
          {isSettingCreatorCountries ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </PopupOverlay>
  );
};

export default SetCreatorCountriesPopup;

const checkboxGroupClass = style({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
  gap: '10px 16px',
  margin: '16px 0 24px',
});

const checkboxLabelClass = style({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: 'rgba(255,255,255,0.85)',
  fontSize: 14,
  cursor: 'pointer',
  $nest: {
    'input[type="checkbox"]': {
      accentColor: '#0005dc',
      width: 15,
      height: 15,
      cursor: 'pointer',
    },
  },
});
